import { isUuidV7 } from '@nest/ids';

/**
 * The paths the shell links to, as a literal union.
 *
 * ProLayout hands a menu renderer a plain `string`, while `<Link to>` only accepts the
 * router's own route literals. This module is the one seam between the two: it lets
 * `isAppPath` narrow the string instead of casting it, so a renamed route is a compile error
 * here rather than a menu entry that navigates nowhere.
 *
 * `/set-password` is in the list although no menu links to it: the forced-password-change
 * guard sends a signed-in account there, and the post-login redirect mapper has to be able
 * to navigate back to it (`useRedirectNavigation`).
 *
 * `/settings` is unlinked the same way since the 系统设置 entry left the shell's menu: the
 * appearance page still resolves on a direct visit, it is just no longer advertised. Keep it
 * here so a visit is not mistaken for an unknown route.
 *
 * 系统管理页面使用 `/system`，运营管理页面使用 `/operations`，后台接口使用 `/api/admin`。
 */
export const appPaths = [
  '/',
  '/system/users',
  '/operations/enterprises',
  '/operations/daily-operations',
  '/operations/certifications',
  '/operations/opportunities',
  '/operations/connections',
  '/operations/memberships',
  '/operations/invitation-codes',
  '/operations/country-regions',
  '/operations/imports',
  '/operations/industries',
  '/operations/company-types',
  '/operations/feedback',
  '/operations/announcements',
  '/system/roles',
  '/system/positions',
  '/system/departments',
  '/system/dicts',
  '/system/configs',
  '/system/sms-configs',
  '/system/legal',
  '/system/logs',
  '/system/tasks',
  '/system/menus',
  '/system/files',
  '/system/storage-disks',
  '/system/monitor/sessions',
  '/system/monitor/redis',
  '/system/monitor/service',
  '/profile',
  '/tenants',
  '/inbox',
  '/set-password',
  '/settings',
] as const;

export type AppPath = (typeof appPaths)[number];

export const isAppPath = (path: string | undefined): path is AppPath =>
  path !== undefined && (appPaths as readonly string[]).includes(path);

export const matchEnterpriseId = (path: string): string | undefined => {
  const id = /^\/(?:operations|system)\/enterprises\/([^/]+)\/?$/.exec(
    path,
  )?.[1];
  return isUuidV7(id) ? id : undefined;
};

export const matchUserId = (path: string): string | undefined => {
  const id = /^\/system\/users\/([^/]+)$/.exec(path)?.[1];
  return isUuidV7(id) ? id : undefined;
};

/**
 * A login redirect target, reduced to something the router will accept.
 *
 * The value arrives from a URL a user can type, so it is an open-redirect attack surface:
 * only same-origin absolute paths survive, and `//evil.example` (which a browser reads as
 * a protocol-relative URL) is rejected too.
 */
export const sanitizeRedirect = (value: unknown): string =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : '/';
